/**
 * YTruncationRecovery.ts
 * Recuperación de coordenadas Y truncadas en documentos PTEL de Andalucía.
 *
 * Detecta y corrige truncamientos del prefijo "4" (o "41") en coordenadas
 * UTM30 Y (northing), así como pérdida de dígitos finales y errores de
 * interpretación decimal.
 *
 * Casos reales: Quéntar (y: 136180 → 4136180), Tíjola, etc.
 *
 * @module YTruncationRecovery
 * @version 1.0.0
 * @date 2026-02-08
 * @task F2.02
 */

import {
  ANDALUCIA_Y_RANGE,
  EXPECTED_Y_DIGITS,
  ANDALUCIA_Y_PREFIX,
} from './CoordinateErrorClassification';

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Tipo de truncamiento detectado.
 */
export type TruncationType =
  | 'sin_prefijo_4'          // 6 dígitos → prefijo "4"
  | 'sin_prefijo_41'         // ≤5 dígitos → prefijo "41"
  | 'ultimo_digito_perdido'  // 6 dígitos empezando por "41" → append "0"
  | 'dos_digitos_perdidos'   // 5 dígitos empezando por "4" → append "00"
  | 'decimal_como_entero';   // Decimal con parte entera 6 dígitos "41..." → concatenar

/**
 * Confianza de la recuperación.
 */
export type RecoveryConfidence = 'high' | 'medium' | 'low';

/**
 * Resultado de la recuperación de Y truncada.
 */
export interface YRecoveryResult {
  /** Indica que hubo truncamiento y se recuperó */
  readonly recovered: true;
  /** Valor Y original */
  readonly originalY: number;
  /** Valor Y corregido */
  readonly correctedY: number;
  /** Tipo de truncamiento detectado */
  readonly truncationType: TruncationType;
  /** Confianza en la corrección */
  readonly confidence: RecoveryConfidence;
  /** Descripción legible del cambio */
  readonly message: string;
}

/**
 * Entrada para la recuperación: par X/Y numérico o string.
 */
export interface CoordInput {
  readonly x: number;
  readonly y: number;
}

// ============================================================================
// CONSTANTES INTERNAS
// ============================================================================

const Y_MIN = ANDALUCIA_Y_RANGE.min; // 4_050_000
const Y_MAX = ANDALUCIA_Y_RANGE.max; // 4_290_000

/** Comprueba si un valor Y corregido cae dentro del rango de Andalucía. */
function inRange(y: number): boolean {
  return y >= Y_MIN && y <= Y_MAX;
}

// ============================================================================
// ESTRATEGIAS DE RECUPERACIÓN (orden de prioridad)
// ============================================================================

/**
 * S5: Decimal como entero.
 * Y tiene decimal, parte entera 6 dígitos empezando por "41".
 * Ejemplo: 413761.8 → 4137618
 */
function tryDecimalAsInteger(y: number): YRecoveryResult | null {
  if (Number.isInteger(y)) return null;

  const intPart = Math.floor(y);
  const intStr = intPart.toString();

  // Solo si parte entera tiene 6 dígitos y empieza por "41"
  if (intStr.length !== 6 || !intStr.startsWith('41')) return null;

  // Extraer parte decimal como string (sin "0.")
  const decStr = y.toString().split('.')[1];
  if (!decStr) return null;

  const candidateStr = intStr + decStr;
  const candidate = Number(candidateStr);

  if (!inRange(candidate)) return null;

  return {
    recovered: true,
    originalY: y,
    correctedY: candidate,
    truncationType: 'decimal_como_entero',
    confidence: 'high',
    message: `Y decimal reinterpretada: ${y} → ${candidate} (punto decimal era artefacto)`,
  };
}

/**
 * S1: Prefijo "4".
 * Y tiene 6 dígitos y NO empieza por "41" → añadir "4" al inicio.
 * Ejemplo: 136180 → 4136180
 */
function tryPrefix4(y: number): YRecoveryResult | null {
  if (!Number.isInteger(y) || y <= 0) return null;

  const yStr = y.toString();
  if (yStr.length !== 6) return null;

  // Si empieza por "41", es más probable que sea dígito final perdido (S3)
  if (yStr.startsWith('41')) return null;

  const candidate = Number(ANDALUCIA_Y_PREFIX + yStr);
  if (!inRange(candidate)) return null;

  return {
    recovered: true,
    originalY: y,
    correctedY: candidate,
    truncationType: 'sin_prefijo_4',
    confidence: 'high',
    message: `Y truncada: ${y} → ${candidate} (prefijo "${ANDALUCIA_Y_PREFIX}" añadido)`,
  };
}

/**
 * S3: Último dígito perdido.
 * Y tiene 6 dígitos y empieza por "41" → append "0".
 * Ejemplo: 413618 → 4136180
 */
function tryTrailingZero(y: number): YRecoveryResult | null {
  if (!Number.isInteger(y) || y <= 0) return null;

  const yStr = y.toString();
  if (yStr.length !== 6 || !yStr.startsWith('41')) return null;

  const candidate = y * 10; // append "0"
  if (!inRange(candidate)) return null;

  return {
    recovered: true,
    originalY: y,
    correctedY: candidate,
    truncationType: 'ultimo_digito_perdido',
    confidence: 'medium',
    message: `Y con dígito final perdido: ${y} → ${candidate} (dígito "0" inferido)`,
  };
}

/**
 * S2: Prefijo "41".
 * Y tiene 5 dígitos o menos y NO empieza por "4" → añadir "41" al inicio.
 * Ejemplo: 36180 → 4136180, 75000 → 4175000
 */
function tryPrefix41(y: number): YRecoveryResult | null {
  if (!Number.isInteger(y) || y <= 0) return null;

  const yStr = y.toString();
  if (yStr.length > 5) return null;

  // Si empieza por "4", es más probable que sea trailing zeros (S4)
  if (yStr.startsWith('4')) return null;

  const candidate = Number('41' + yStr);
  if (!inRange(candidate)) return null;

  return {
    recovered: true,
    originalY: y,
    correctedY: candidate,
    truncationType: 'sin_prefijo_41',
    confidence: 'medium',
    message: `Y muy truncada: ${y} → ${candidate} (prefijo "41" añadido)`,
  };
}

/**
 * S4: Dos dígitos finales perdidos.
 * Y tiene 5 dígitos y empieza por "4" → append "00".
 * Ejemplo: 41361 → 4136100
 */
function tryTrailingZeros(y: number): YRecoveryResult | null {
  if (!Number.isInteger(y) || y <= 0) return null;

  const yStr = y.toString();
  if (yStr.length !== 5 || !yStr.startsWith('4')) return null;

  const candidate = y * 100; // append "00"
  if (!inRange(candidate)) return null;

  return {
    recovered: true,
    originalY: y,
    correctedY: candidate,
    truncationType: 'dos_digitos_perdidos',
    confidence: 'low',
    message: `Y con 2 dígitos finales perdidos: ${y} → ${candidate} (dígitos "00" inferidos)`,
  };
}

// ============================================================================
// API PÚBLICA
// ============================================================================

/**
 * Cadena de estrategias de recuperación, en orden de prioridad.
 * La primera que produzca un resultado válido gana.
 */
const RECOVERY_STRATEGIES = [
  tryDecimalAsInteger,  // S5: máxima prioridad (información completa en decimal)
  tryPrefix4,           // S1: caso más común
  tryTrailingZero,      // S3: 6 dígitos con "41..." + trailing 0
  tryPrefix41,          // S2: 5 dígitos, prefijo "41"
  tryTrailingZeros,     // S4: 5 dígitos con "4..." + trailing 00
] as const;

/**
 * Intenta recuperar una coordenada Y truncada.
 *
 * Aplica secuencialmente 5 estrategias de recuperación hasta encontrar
 * una que produzca una Y válida dentro del rango de Andalucía.
 *
 * @param input - Par de coordenadas (X/Y numéricas)
 * @returns Resultado de recuperación o null si no se detecta truncamiento
 */
export function recoverTruncatedY(input: CoordInput): YRecoveryResult | null {
  const { y } = input;

  // Y ya tiene 7+ dígitos en parte entera → no truncada
  const intDigits = Math.floor(Math.abs(y)).toString().length;
  if (intDigits >= EXPECTED_Y_DIGITS && Number.isInteger(y)) return null;

  // Y negativa o cero → no es UTM válida
  if (y <= 0) return null;

  // Y ya está en rango andaluz → no truncada
  if (Number.isInteger(y) && inRange(y)) return null;

  for (const strategy of RECOVERY_STRATEGIES) {
    const result = strategy(y);
    if (result !== null) return result;
  }

  return null;
}

/**
 * Versión simplificada: devuelve la coordenada corregida o el par original.
 *
 * @param input - Par de coordenadas
 * @returns Par con Y corregida si se detectó truncamiento, original si no
 */
export function fixTruncatedY(input: CoordInput): CoordInput {
  const result = recoverTruncatedY(input);
  if (!result) return input;
  return { x: input.x, y: result.correctedY };
}

/**
 * Detección rápida: ¿la Y parece truncada?
 */
export function isTruncatedY(input: CoordInput): boolean {
  return recoverTruncatedY(input) !== null;
}
